import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Button,
  Box,
  Typography,
  RadioGroup,
  Radio,
  FormControlLabel,
  Paper,
  InputAdornment,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LinkIcon from "@mui/icons-material/Link";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DropdownTextField from "../form-components/DropdownTextField";
import MediaPreviewOverlay from "../common/MediaPreviewOverlay";
import { MEDIA_TYPES, getPositionLabels, emptyPosition, positionIsComplete, positionIsPartial, SectionLabel } from "./PortalShared";
import PositionPreviewModal from "./PositionPreviewModal";
import LocalText from "../common/LocalText";
import { useLocalText } from "../common/useLocalText";
import { hasRequiredName, isValidUrl } from '../../utils/validators';
import useUrlValidation from '../../hooks/useUrlValidation';
import UrlStatusIcon from '../common/UrlStatusIcon';

const emptyValidation = () => ({ portalLinkOk: true, thumbnailOk: true });

const CreatePortalModal = ({ open, onClose, onSave, positionCount = 3, boothName }) => {
  const positionLabels = getPositionLabels(positionCount);

  const [sharedTitle, setSharedTitle] = useState([{ localeId: "en_us", value: "" }]);
  const [sharedMedia, setSharedMedia] = useState({
    mediaTypeId: 1,
    linkLocalizations: [{ localeId: "en_us", mediaLink: "" }],
  });

  const [positions, setPositions] = useState(() => Array.from({ length: positionCount }, emptyPosition));
  const [activePosition, setActivePosition] = useState(0);
  const [linkPreviewOpen, setLinkPreviewOpen] = useState(false);
  const [positionPreviewOpen, setPositionPreviewOpen] = useState(false);

  // Persists async validation results per position across position switches.
  const [positionValidation, setPositionValidation] = useState(() =>
    Array.from({ length: positionCount }, emptyValidation)
  );

  useEffect(() => {
    if (open) {
      setActivePosition(0);
      setSharedTitle([{ localeId: "en_us", value: "" }]);
      setSharedMedia({ mediaTypeId: 1, linkLocalizations: [{ localeId: "en_us", mediaLink: "" }] });
      setPositions(Array.from({ length: positionCount }, emptyPosition));
      setPositionValidation(Array.from({ length: positionCount }, emptyValidation));
      setLinkPreviewOpen(false);
    }
  }, [open, positionCount]);

  const position = positions[activePosition] ?? emptyPosition();

  const updatePosition = (field, value) =>
    setPositions((prev) => prev.map((p, i) => (i === activePosition ? { ...p, [field]: value } : p)));

  const mediaType = MEDIA_TYPES.find((m) => m.value === sharedMedia.mediaTypeId)?.type ?? "image";
  const { status: portalLinkStatus } = useUrlValidation(position.portalLink, 'url');

  const [mediaUrlsValid, setMediaUrlsValid] = useState(true);

  // Ref keeps activePosition current without making callbacks depend on it.
  const activePositionRef = useRef(activePosition);
  activePositionRef.current = activePosition;

  // Persist the active position's portal-link async result into positionValidation.
  // Only 'idle' (empty) and 'valid' pass — 'checking' blocks too, same as DropdownTextField.
  useEffect(() => {
    setPositionValidation((pv) =>
      pv.map((v, i) =>
        i === activePosition
          ? { ...v, portalLinkOk: portalLinkStatus === 'valid' || portalLinkStatus === 'idle' }
          : v
      )
    );
  }, [portalLinkStatus, activePosition]);

  // Stable callback (deps []) — reads activePositionRef at call-time.
  const handleThumbnailValidation = useCallback((allValid) => {
    setPositionValidation((pv) =>
      pv.map((v, i) => i === activePositionRef.current ? { ...v, thumbnailOk: allValid } : v)
    );
  }, []);

  const handleMediaValidation = useCallback((allValid) => setMediaUrlsValid(allValid), []);

  const handleSubmit = () => {
    onSave(sharedTitle, positions, sharedMedia);
    onClose();
  };

  const hasSharedMedia = sharedMedia.linkLocalizations.some((l) => (l.mediaLink ?? '').trim().length > 0);

  // Primary: persisted async result (visited positions).
  // Fallback: format check (catches pre-loaded bad URLs on unvisited positions).
  const allPortalLinksValid = positions.every((pos, i) => {
    const link = (pos?.portalLink ?? '').trim();
    if (!link) return true;
    if (positionValidation[i]?.portalLinkOk === false) return false;
    return isValidUrl(link);
  });

  const allThumbnailsValid = positions.every((pos, i) => {
    if (positionValidation[i]?.thumbnailOk === false) return false;
    return (pos.thumbnailLocalizations ?? []).every(
      (t) => !(t.mediaLink ?? '').trim() || isValidUrl(t.mediaLink)
    );
  });

  const noPartialPositions = !positions.some(positionIsPartial);

  const isValid =
    hasRequiredName(sharedTitle) &&
    positions.some(positionIsComplete) &&
    noPartialPositions &&
    allPortalLinksValid &&
    allThumbnailsValid &&
    mediaUrlsValid &&
    hasSharedMedia;

  const positionPreviewLabel = useLocalText("Position Preview");
  const portalLinkPlaceholder = useLocalText("https://...");

  const portalLinkHelperText = (() => {
    if (portalLinkStatus === 'checking') return 'Checking…';
    if (portalLinkStatus === 'invalid') {
      return isValidUrl(position.portalLink) ? 'URL could not be reached' : 'URL must start with http:// or https://';
    }
    if (portalLinkStatus === 'valid') return 'Valid';
    // Empty — only flag as required once this position has a thumbnail and nothing else
    return positionIsPartial(position) ? 'Required — thumbnail already set for this position' : '';
  })();

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden", maxHeight: { xs: '95vh', sm: '85vh' }, m: { xs: 0.5, sm: 2 } } }}
      >
        <DialogTitle sx={{ bgcolor: "primary.main", color: "white", pr: 6, py: 2.5 }}>
          <Typography variant="h6" fontWeight={700} component="span" sx={{ display: "block" }}>
            <LocalText>New Portal</LocalText>
          </Typography>
          {boothName && (
            <Typography component="span" variant="subtitle2" sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 600, mt: 0.25, display: "block" }}>
              {boothName}
            </Typography>
          )}
          <Typography component="span" variant="caption" sx={{ color: "rgba(255,255,255,0.6)", mt: 0.25, display: "block" }}>
            <LocalText>Title and media shared · Portal link and thumbnail per position</LocalText>
          </Typography>
          <IconButton onClick={(e) => { e.currentTarget.blur(); onClose(); }} size="small" sx={{ position: "absolute", right: 10, top: 12, color: "white" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: { xs: 2, sm: 3 }, overflow: "auto" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

            {/* Shared title */}
            <Box>
              <SectionLabel required>Title</SectionLabel>
              <DropdownTextField
                label="Title"
                values={sharedTitle}
                attribute="value"
                onChange={setSharedTitle}
                required
              />
            </Box>

            <Divider />

            {/* Position navigation */}
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                <SectionLabel>Position</SectionLabel>
                <Button
                  size="small"
                  startIcon={<VisibilityIcon fontSize="small" />}
                  onClick={(e) => { e.currentTarget.blur(); setPositionPreviewOpen(true); }}
                  sx={{ textTransform: "none", fontSize: "0.75rem", color: "primary.main", p: 0.5 }}
                >
                  {positionPreviewLabel}
                </Button>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                {positionLabels.map((label, index) => {
                  const isActive = activePosition === index;
                  const pos = positions[index];
                  const pv = positionValidation[index];

                  // hasInvalidUrl uses persisted async results — catches failures on inactive positions too.
                  const hasInvalidUrl = !isActive && (
                    pv?.portalLinkOk === false ||
                    pv?.thumbnailOk === false
                  );
                  const isPartial = !isActive && positionIsPartial(pos);
                  const isComplete = !isActive && positionIsComplete(pos);

                  // Priority: invalid URL (red) > partial (orange) > complete/empty
                  const borderColor = isActive ? "primary.main"
                    : hasInvalidUrl ? "error.main"
                    : isPartial     ? "#ed6c02"
                    : "primary.lighter";

                  return (
                    <Button
                      key={label}
                      onClick={() => setActivePosition(index)}
                      size="small"
                      sx={{
                        flex: 1,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: isActive ? 700 : 500,
                        bgcolor: isActive ? "primary.main" : "transparent",
                        color: isActive ? "white" : "primary.main",
                        border: "2px solid",
                        borderColor,
                        py: 0.75,
                        position: "relative",
                        "&:hover": {
                          bgcolor: isActive ? "primary.dark" : "rgba(10,50,109,0.06)",
                          borderColor: hasInvalidUrl ? "error.dark" : isPartial ? "#e65100" : "primary.main",
                        },
                      }}
                    >
                      {label}
                      {/* Red dot — invalid URL (highest priority) */}
                      {hasInvalidUrl && (
                        <Box sx={{ position: "absolute", top: 5, right: 6, width: 7, height: 7, borderRadius: "50%", bgcolor: "error.main" }} />
                      )}
                      {/* Orange dot — partial, no async failures */}
                      {isPartial && !hasInvalidUrl && (
                        <Box sx={{ position: "absolute", top: 5, right: 6, width: 7, height: 7, borderRadius: "50%", bgcolor: "#ed6c02" }} />
                      )}
                      {/* Green dot — both fields complete, no issues */}
                      {isComplete && !hasInvalidUrl && !isPartial && (
                        <Box sx={{ position: "absolute", top: 5, right: 6, width: 7, height: 7, borderRadius: "50%", bgcolor: "success.main" }} />
                      )}
                    </Button>
                  );
                })}
              </Box>
            </Box>

            {/* Per-position fields — re-mount on switch to reset DropdownTextField internal state */}
            <Box key={activePosition} sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>

              <SectionLabel required>Portal Link</SectionLabel>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={portalLinkPlaceholder}
                  value={position.portalLink}
                  onChange={(e) => updatePosition("portalLink", e.target.value)}
                  error={portalLinkStatus === 'invalid'}
                  helperText={portalLinkHelperText}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LinkIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <UrlStatusIcon status={portalLinkStatus} />
                      </InputAdornment>
                    ),
                  }}
                />
                <IconButton
                  size="small"
                  disabled={!position.portalLink}
                  onClick={(e) => { e.currentTarget.blur(); setLinkPreviewOpen(true); }}
                  sx={{ color: "text.secondary", "&:hover": { color: "primary.main" } }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Box>

              <SectionLabel required>Thumbnail</SectionLabel>
              <DropdownTextField
                label="Image URL"
                values={position.thumbnailLocalizations}
                attribute="mediaLink"
                onChange={(v) => updatePosition("thumbnailLocalizations", v)}
                previewable
                mediaType="image"
                urlValidationType="image"
                onValidationChange={handleThumbnailValidation}
                required={positionIsPartial(position)}
              />

            </Box>

            <Divider />

            {/* Shared media */}
            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  color: "primary.main",
                  mb: 1.5,
                }}
              >
                <LocalText>Main Media — shared for all positions</LocalText>
              </Typography>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "grey.50", mb: 2 }}>
                <SectionLabel>Media Type</SectionLabel>
                <RadioGroup
                  row
                  value={sharedMedia.mediaTypeId}
                  onChange={(e) => setSharedMedia((p) => ({ ...p, mediaTypeId: Number(e.target.value) }))}
                  sx={{ mt: 0.5 }}
                >
                  {MEDIA_TYPES.map((m) => (
                    <FormControlLabel key={m.value} value={m.value} control={<Radio size="small" />} label={<LocalText>{m.label}</LocalText>} sx={{ mr: 3 }} />
                  ))}
                </RadioGroup>
              </Paper>

              <SectionLabel required>Media URL</SectionLabel>
              <DropdownTextField
                label="Media URL"
                values={sharedMedia.linkLocalizations}
                attribute="mediaLink"
                onChange={(v) => setSharedMedia((p) => ({ ...p, linkLocalizations: v }))}
                previewable
                mediaType={mediaType}
                urlValidationType={mediaType}
                onValidationChange={handleMediaValidation}
                required
              />
            </Box>

          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", p: 3, bgcolor: "grey.50" }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!isValid}
            size="large"
            sx={{ px: 8, py: 1.5, fontWeight: 700, borderRadius: 2, width: { xs: "100%", sm: "50%" } }}
          >
            <LocalText>Save Portals</LocalText>
          </Button>
        </DialogActions>
      </Dialog>

      <MediaPreviewOverlay
        open={linkPreviewOpen}
        onClose={() => setLinkPreviewOpen(false)}
        type="link"
        url={position.portalLink}
      />

      <PositionPreviewModal
        open={positionPreviewOpen}
        onClose={() => setPositionPreviewOpen(false)}
        imageUrl={sharedMedia.linkLocalizations[0]?.mediaLink ?? ""}
        positionThumbnails={positions.map((p) => p.thumbnailLocalizations[0]?.mediaLink ?? "")}
      />
    </>
  );
};

export default CreatePortalModal;
